import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  dealListTotalsSchema,
  dealSchema,
  type Deal,
  type DealListTotals,
} from '@forecastify/shared';
import {
  Button,
  DropdownMenu,
  Icon,
  Tabs,
  Toaster,
  cn,
  type Toast,
  type ViewSwitch,
} from '@forecastify/ui';
import { useT } from '../../i18n';
import { useLiveInvalidation } from '../../live/use-live';
import { DEAL_TOPICS } from '../../live/topics';
import { useSession } from '../../session';
import { useGoUp, useWorkspaceNavigate } from '../../workspace-path';
import { Breadcrumb } from '../breadcrumb';
import { ListHeader, usePageHeader } from '../page-header';
import { parseList, totalOf, usePermissionSet } from '../permissions-hook';
import { ScreenState, failureOf, type ScreenFailure } from '../screen-state';
import { useFirstLoad } from '../use-first-load';
import { useListQuery } from '../use-list-query';
import { DealBoardScreen } from './board-screen';
import { LostDialog, WonDialog } from './close-dialogs';
import { dealMoney, dealRefusal, useDealFeeds, type DealFeeds } from './deal-kit';
import { DealOverviewTab } from './deal-overview-tab';
import { DealPlanTab } from './deal-plan-tab';
import { DealResourceTab } from './deal-resource-tab';
import { DealScenarioTab } from './deal-scenario-tab';
import { DealsListScreen, type DealRowActions } from './deals-list-screen';
import { DealDemandScreen } from './demand-screen';
import { DealForecastScreen } from './forecast-screen';
import { GenerateWizard } from './generate-wizard';
import { NewDealDialog } from './new-deal-dialog';
import { useDealRecord } from './use-deal-record';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · `/deals` and `/deals/:ref`. PHASE-6.md §1.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `/deals` is one route with four modes — *Lista · Pipeline · Forecast · Erőforrás-igény* — kept
 * in `?mode=` so a link opens the same view. The route owns the dialogs every mode shares (new
 * deal, won, lost, generation), so a row menu in the list and a card menu on the board do the same
 * thing by calling the same handler.
 *
 * ⚠️ **No side drawer** where the mockup offers *open in drawer*: the record is a page, and a
 * drawer copy would be a second surface for every tab. Recorded as a deviation (`decisions.md`).
 */

type Mode = 'list' | 'kanban' | 'forecast' | 'demand';
const MODES: readonly Mode[] = ['list', 'kanban', 'forecast', 'demand'];

function modeLabel(t: ReturnType<typeof useT>, mode: Mode): string {
  if (mode === 'kanban') return t('deals.mode.kanban');
  if (mode === 'forecast') return t('deals.mode.forecast');
  if (mode === 'demand') return t('deals.mode.demand');
  return t('deals.mode.list');
}

/**
 * The dialogs a deal row can open from anywhere — list, board, record. One state machine, so the
 * won dialog's *continue with generation* can hand the same deal to the wizard.
 */
function useDealDialogs(feeds: DealFeeds, onChanged: () => void) {
  const t = useT();
  const session = useSession();
  const go = useWorkspaceNavigate();
  const [won, setWon] = useState<Deal | null>(null);
  const [lost, setLost] = useState<Deal | null>(null);
  const [generating, setGenerating] = useState<string | null>(null);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);

  const toast = useCallback((title: string, variant: Toast['variant'] = 'success') => {
    setToasts((current) => [...current, { id: `${String(Date.now())}-${title}`, title, variant }]);
  }, []);

  const call = useCallback(
    async (path: string, method: string, body?: unknown): Promise<boolean> => {
      const response = await session
        .request(path, { method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) })
        .catch(() => null);
      if (response === null) {
        toast(t('error.unavailable'), 'error');
        return false;
      }
      if (!response.ok) {
        toast(await dealRefusal(response, t), 'error');
        return false;
      }
      return true;
    },
    [session, t, toast],
  );

  /**
   * ⚠️⚠️ **STABLE, BECAUSE THE RECORD'S HEADER DEPENDS ON IT.** `usePageHeader` re-renders the
   * shell whenever its dependencies change; an `actions` object rebuilt every render made the header
   * set itself on every render — *Maximum update depth exceeded*, and a page whose tabs and back
   * button changed the address but never repainted. `onChanged` comes in as a fresh arrow each
   * render, so it is read through a ref rather than listed.
   */
  const changedRef = useRef(onChanged);
  changedRef.current = onChanged;
  const actions: DealRowActions = useMemo(
    () => ({
      onOpen: (deal) => {
        go(`/deals/${deal.identifier}`);
      },
      onFavourite: (deal, on) => {
        void call(`/deals/${deal.id}/favourite`, on ? 'PUT' : 'DELETE').then((ok) => {
          if (ok) changedRef.current();
        });
      },
      onWon: setWon,
      onLost: setLost,
      onReopen: (deal) => {
        void call(`/deals/${deal.id}/reopen`, 'POST').then((ok) => {
          if (ok) changedRef.current();
        });
      },
      onGenerate: (deal) => {
        setGenerating(deal.id);
      },
      onDelete: (deal) => {
        void call(`/deals/${deal.id}`, 'DELETE').then((ok) => {
          if (!ok) return;
          toast(t('deals.toast.deleted'));
          changedRef.current();
        });
      },
    }),
    [go, call, toast, t],
  );

  const dialogs = (
    <>
      {won === null ? null : (
        <WonDialog
          open
          deal={won}
          feeds={feeds}
          onClose={() => {
            setWon(null);
          }}
          onDone={(detail, generate) => {
            setWon(null);
            onChanged();
            if (generate) setGenerating(detail.id);
          }}
        />
      )}
      {lost === null ? null : (
        <LostDialog
          open
          deal={lost}
          feeds={feeds}
          onClose={() => {
            setLost(null);
          }}
          onDone={() => {
            setLost(null);
            onChanged();
          }}
        />
      )}
      {generating === null ? null : (
        <GenerateWizard
          open
          dealRef={generating}
          feeds={feeds}
          onClose={() => {
            setGenerating(null);
          }}
          onDone={() => {
            setGenerating(null);
            onChanged();
          }}
        />
      )}
      <Toaster
        toasts={[...toasts]}
        label={t('title.deals')}
        dismissLabel={t('common.close')}
        onDismiss={(id) => {
          setToasts((current) => current.filter((entry) => entry.id !== id));
        }}
      />
    </>
  );

  return { actions, dialogs, call, toast };
}

export function DealsRoute() {
  const session = useSession();
  const t = useT();
  const permissions = usePermissionSet();
  const go = useWorkspaceNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const mode: Mode = MODES.find((entry) => entry === searchParams.get('mode')) ?? 'list';
  const canManage = permissions.has('deal.manage');
  const feeds = useDealFeeds();

  /* 3 Nov 2026 · remembered per workspace and member (`list-memory.ts`). */
  const query = useListQuery({ view: 'open' }, [], undefined, { persistKey: 'deals' });
  const { pages, params } = query;
  const { settle: settlePage } = pages;
  const [deals, setDeals] = useState<readonly Deal[]>([]);
  const [totals, setTotals] = useState<DealListTotals | null>(null);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [creating, setCreating] = useState(searchParams.get('new') === '1');
  /** Bumped after a write so a non-list mode reloads its own data. */
  const [revision, setRevision] = useState(0);

  usePageHeader(() => <ListHeader title={t('title.deals')} />, []);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }
    if (mode !== 'list') return;
    const text = params.toString();
    const response = await session
      .request(`/deals${text === '' ? '' : `?${text}`}`)
      .catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }
    const body: unknown = await response.json();
    const parsed = parseList(dealSchema, body);
    if (parsed === null) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }
    setFailure(null);
    setDeals(parsed);
    const sums = dealListTotalsSchema.safeParse((body as { totals?: unknown }).totals);
    setTotals(sums.success ? sums.data : null);
    settlePage(totalOf(body));
  }, [session, params, settlePage, mode]);

  const loading = useFirstLoad(reload);
  useLiveInvalidation(DEAL_TOPICS, reload);

  const changed = useCallback(() => {
    setRevision((current) => current + 1);
    void reload();
  }, [reload]);
  const { actions, dialogs, call, toast } = useDealDialogs(feeds, changed);

  const modeSwitch: ViewSwitch = {
    value: mode,
    items: MODES.map((entry) => ({
      value: entry,
      label: modeLabel(t, entry),
      icon:
        entry === 'list'
          ? 'list'
          : entry === 'kanban'
            ? 'kanban'
            : entry === 'forecast'
              ? 'chart-column'
              : 'users',
    })),
    onChange: (value) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value === 'list') next.delete('mode');
          else next.set('mode', value);
          return next;
        },
        { replace: true },
      );
    },
  };

  /* The sidebar's `+` arrives as `?new=1`; it opens the dialog once and leaves the address. */
  useEffect(() => {
    if (searchParams.get('new') !== '1') return;
    setCreating(true);
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('new');
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  const onCreate = (): void => {
    setCreating(true);
  };
  const modeProps = { feeds, modeSwitch, canManage, actions, onCreate };

  /* ⚠️ The marker is the ROUTE's, outside `ScreenState`, so every mode and every state of
     `/deals` answers to it — the modes set none of their own. */
  return (
    <div data-screen="deals" className="flex min-h-0 flex-1 flex-col">
      <ScreenState failure={failure} onRetry={() => void reload()}>
        {mode === 'kanban' ? (
          <DealBoardScreen key={revision} {...modeProps} />
        ) : mode === 'forecast' ? (
          <DealForecastScreen key={revision} {...modeProps} />
        ) : mode === 'demand' ? (
          <DealDemandScreen key={revision} {...modeProps} />
        ) : (
          <DealsListScreen
            deals={deals}
            totals={totals}
            feeds={feeds}
            query={query}
            loading={loading}
            modeSwitch={modeSwitch}
            canManage={canManage}
            actions={actions}
            onCreate={onCreate}
            onOmitMany={(ids) => {
              void (async () => {
                let done = 0;
                for (const id of ids) {
                  const deal = deals.find((candidate) => candidate.id === id);
                  if (deal === undefined) continue;
                  if (
                    await call(`/deals/${id}`, 'PATCH', {
                      version: deal.version,
                      forecastOverride: 'omitted',
                    })
                  ) {
                    done += 1;
                  }
                }
                toast(t('deals.toast.omitted', { count: done }));
                changed();
              })();
            }}
            page={{
              hasPrevious: pages.hasPrevious,
              hasNext: pages.hasNext,
              onPrevious: pages.previous,
              onNext: pages.next,
              index: pages.index,
              count: pages.count,
              total: pages.total,
              onGoTo: pages.goTo,
              pageSize: pages.limit,
            }}
          />
        )}
        <NewDealDialog
          open={creating}
          feeds={feeds}
          defaultOwnerId={
            session.workspaces.find((workspace) => workspace.id === session.workspaceId)
              ?.memberId ?? null
          }
          onClose={() => {
            setCreating(false);
          }}
          onCreated={(deal) => {
            toast(t('deals.toast.created'));
            go(`/deals/${deal.identifier}`);
          }}
        />
        {dialogs}
      </ScreenState>
    </div>
  );
}

/* ── the record ───────────────────────────────────────────────────────────────────────────── */

type Tab = 'overview' | 'scenario' | 'plan' | 'resource';
const TABS: readonly Tab[] = ['overview', 'scenario', 'plan', 'resource'];

export function DealPageRoute() {
  const { ref = '' } = useParams();
  const t = useT();
  const goUp = useGoUp();
  const back = useCallback(() => {
    goUp('/deals');
  }, [goUp]);
  const record = useDealRecord(ref);
  const feeds = useDealFeeds();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: Tab = TABS.find((entry) => entry === searchParams.get('tab')) ?? 'overview';
  const { deal } = record;
  const { actions, dialogs } = useDealDialogs(feeds, () => void record.reload());

  const setTab = useCallback(
    (next: Tab) => {
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          if (next === 'overview') params.delete('tab');
          else params.set('tab', next);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  usePageHeader(
    () =>
      deal === null ? null : (
        <>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={back}
            className="-ml-1.5"
            aria-label={t('deal.back')}
          >
            <Icon name="chevron-left" size={16} />
          </Button>
          <span className="text-muted-foreground font-mono text-[12px] tabular-nums">
            {deal.identifier}
          </span>
          <Breadcrumb crumbs={[{ label: deal.name }]} />
          <div className="flex-1" />
          <span className="text-muted-foreground hidden text-[12.5px] tabular-nums md:inline">
            {dealMoney(deal.value, deal.currencyCode)}
          </span>
          {deal.canManage && deal.statusCategory === 'open' ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive"
                onClick={() => {
                  actions.onLost(deal);
                }}
              >
                <Icon name="x" size={14} />
                {t('deals.action.lost')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  actions.onWon(deal);
                }}
              >
                <Icon name="circle-check" size={14} />
                {t('deals.action.won')}
              </Button>
            </>
          ) : null}
          {deal.canManage ? (
            <Button
              size="sm"
              onClick={() => {
                actions.onGenerate(deal);
              }}
            >
              <Icon name="sparkles" size={14} />
              {t('deals.action.generate')}
            </Button>
          ) : null}
          <DropdownMenu
            label={t('list.rowMenu')}
            items={[
              {
                label: deal.favourite ? t('deals.action.unfavourite') : t('deals.action.favourite'),
                icon: 'star',
                onSelect: () => {
                  actions.onFavourite(deal, !deal.favourite);
                },
              },
              {
                label: t('deal.copy.identifier'),
                icon: 'copy',
                onSelect: () => {
                  void navigator.clipboard.writeText(deal.identifier);
                },
              },
              {
                label: t('deal.copy.link'),
                icon: 'link',
                onSelect: () => {
                  void navigator.clipboard.writeText(window.location.href);
                },
              },
              ...(deal.canManage && deal.statusCategory !== 'open'
                ? [
                    {
                      label: t('deals.action.reopen'),
                      icon: 'refresh-cw' as const,
                      onSelect: () => {
                        actions.onReopen(deal);
                      },
                    },
                  ]
                : []),
              ...(deal.canManage
                ? [
                    {
                      label: t('deals.action.delete'),
                      icon: 'trash' as const,
                      destructive: true,
                      onSelect: () => {
                        actions.onDelete(deal);
                        back();
                      },
                    },
                  ]
                : []),
            ]}
          >
            <Button variant="ghost" size="icon-sm" aria-label={t('list.rowMenu')}>
              <Icon name="ellipsis" size={16} />
            </Button>
          </DropdownMenu>
        </>
      ),
    [deal, t, back, actions],
  );

  const stages = useMemo(
    () => (feeds.config?.statuses ?? []).filter((status) => status.category === 'open'),
    [feeds.config],
  );

  return (
    <div data-screen="deal" className="flex min-h-0 flex-1 flex-col">
      <ScreenState failure={record.failure} onRetry={() => void record.reload()}>
        <div className="flex min-h-0 flex-1 flex-col">
          {deal === null ? null : (
            <>
              <div className="border-b px-4">
                <Tabs
                  value={tab}
                  label={t('title.deal')}
                  variant="underline"
                  onValueChange={(value) => {
                    setTab(value as Tab);
                  }}
                  items={TABS.map((entry) => ({
                    value: entry,
                    label:
                      entry === 'plan'
                        ? t('deal.tab.plan')
                        : entry === 'resource'
                          ? t('deal.tab.resource')
                          : entry === 'scenario'
                            ? t('deal.tab.scenario')
                            : t('deal.tab.overview'),
                    ...(entry === 'plan' && deal.lineItems.length > 0
                      ? { count: deal.lineItems.length }
                      : {}),
                    ...(entry === 'resource' && deal.placeholders.length > 0
                      ? { count: deal.placeholders.length }
                      : {}),
                  }))}
                />
              </div>
              {deal.statusCategory === 'open' ? (
                <StageStepper
                  stages={stages.map((stage) => ({ id: stage.id, name: stage.name }))}
                  currentId={deal.statusId}
                  days={deal.daysInStage}
                  canMove={deal.canManage}
                  onMove={(statusId) => {
                    void record.write('/status', 'POST', { statusId });
                  }}
                />
              ) : null}
              <div className="min-h-0 flex-1 overflow-auto">
                {tab === 'overview' ? (
                  <DealOverviewTab
                    deal={deal}
                    record={record}
                    feeds={feeds}
                    canManage={deal.canManage}
                    onTab={setTab}
                  />
                ) : tab === 'plan' ? (
                  <DealPlanTab
                    deal={deal}
                    record={record}
                    feeds={feeds}
                    canManage={deal.canManage}
                    onTab={setTab}
                  />
                ) : tab === 'resource' ? (
                  <DealResourceTab
                    deal={deal}
                    record={record}
                    feeds={feeds}
                    canManage={deal.canManage}
                    onTab={setTab}
                  />
                ) : (
                  <DealScenarioTab
                    deal={deal}
                    record={record}
                    feeds={feeds}
                    canManage={deal.canManage}
                    onTab={setTab}
                  />
                )}
              </div>
            </>
          )}
        </div>
        {dialogs}
      </ScreenState>
    </div>
  );
}

/**
 * The stage stepper under the tabs — every open status as a chevron, the current one filled with
 * its days. ⚠️ Clicking a step moves the deal there (`POST /deals/:id/status`); won and lost are
 * never steps, they are the header's dialogs.
 */
function StageStepper({
  stages,
  currentId,
  days,
  canMove,
  onMove,
}: {
  readonly stages: readonly { readonly id: string; readonly name: string }[];
  readonly currentId: string;
  readonly days: number;
  readonly canMove: boolean;
  readonly onMove: (statusId: string) => void;
}) {
  const t = useT();
  const at = stages.findIndex((stage) => stage.id === currentId);
  return (
    <div className="flex overflow-x-auto border-b" role="list">
      {stages.map((stage, index) => {
        const current = index === at;
        const passed = index < at;
        return (
          <button
            key={stage.id}
            type="button"
            role="listitem"
            disabled={!canMove || current}
            aria-current={current ? 'step' : undefined}
            onClick={() => {
              onMove(stage.id);
            }}
            className={cn(
              'relative min-w-[120px] flex-1 px-3 py-1.5 text-center text-[12px] whitespace-nowrap transition-colors',
              '[clip-path:polygon(0_0,calc(100%-10px)_0,100%_50%,calc(100%-10px)_100%,0_100%,10px_50%)]',
              current
                ? 'bg-primary text-primary-foreground font-medium'
                : passed
                  ? 'bg-primary/15 text-primary'
                  : 'bg-muted text-muted-foreground',
              canMove && !current ? 'hover:bg-primary/25 cursor-pointer' : 'cursor-default',
            )}
          >
            {current ? `${t('deal.stage.days', { count: days })} · ${stage.name}` : stage.name}
          </button>
        );
      })}
    </div>
  );
}
